import React, { useCallback, useState, useEffect } from 'react';
import { useStreamAuth } from '../../hooks/useStreamAuth';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProfile } from '../../contexts/ProfileContext';
import ProfileDropdown from '../ProfileDropdown';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading, loginWithRedirect, logout } = useStreamAuth();
  const { currentProfile } = useProfile();

  const [headerVisible, setHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > 200) {
        setHeaderVisible(currentScrollY <= lastScrollY);
      } else {
        setHeaderVisible(true);
      }
      setLastScrollY(currentScrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const isHomepage = location.pathname === '/';

  const getHeaderClasses = () => {
    const base = 'py-4 px-6 sticky top-0 z-30 transition-all duration-300';
    const visibility = headerVisible ? 'translate-y-0' : '-translate-y-full';
    const bg = isHomepage
      ? 'bg-gradient-to-b from-black/90 via-black/70 to-transparent'
      : 'bg-gradient-to-b from-primary via-primary/95 to-primary/80';
    return `${base} ${visibility} ${bg}`;
  };

  const handleHomeClick = useCallback(() => navigate('/'), [navigate]);
  const handleEventsClick = useCallback(() => navigate('/events'), [navigate]);
  const handleFightersClick = useCallback(() => navigate('/fighters'), [navigate]);
  const handleMyListsClick = useCallback(() => navigate('/my-lists'), [navigate]);

  const isActive = useCallback(
    path => {
      if (path === '/') return location.pathname === '/';
      return location.pathname.startsWith(path);
    },
    [location.pathname],
  );

  const handleLogin = useCallback(() => {
    loginWithRedirect({
      authorizationParams: {
        redirect_uri: window.location.origin,
        scope: 'openid profile email read:streams write:streams',
      },
    });
  }, [loginWithRedirect]);

  const handleLogout = useCallback(() => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  const handleSearchChange = useCallback(
    e => {
      const value = e.target.value;
      setSearchQuery(value);
      if (value.trim().length > 0) {
        navigate(`/search?q=${encodeURIComponent(value.trim())}`);
      }
    },
    [navigate],
  );

  const handleSearchKeyPress = useCallback(
    e => {
      if (e.key === 'Enter' && searchQuery.trim()) {
        navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      }
    },
    [navigate, searchQuery],
  );

  const navLinkClass = path =>
    `font-display text-lg uppercase tracking-wide ${
      isActive(path) ? 'text-white' : 'text-white/70'
    } hover:text-accent bg-transparent border-0 p-0 cursor-pointer transition-colors`;

  return (
    <header className={getHeaderClasses()}>
      <div className="container flex items-center justify-between mx-auto">
        {/* Logo */}
        <div className="flex items-center cursor-pointer" onClick={handleHomeClick}>
          <img
            src="/assets/images/bkfc_logo.webp"
            alt="BKFC"
            className="h-7 md:h-8 w-auto grayscale brightness-0 invert"
          />
        </div>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center space-x-6">
          <button className={navLinkClass('/')} onClick={handleHomeClick}>
            Home
          </button>
          <button className={navLinkClass('/events')} onClick={handleEventsClick}>
            Events
          </button>
          <button className={navLinkClass('/fighters')} onClick={handleFightersClick}>
            Fighters
          </button>
          <button className={navLinkClass('/my-lists')} onClick={handleMyListsClick}>
            My List
          </button>
        </nav>

        {/* Right side: Search + Auth */}
        <div className="flex items-center space-x-4">
          {/* Desktop expandable search */}
          <div className="relative hidden md:block">
            <div
              className={`flex items-center transition-all duration-300 ease-in-out ${
                isSearchExpanded || searchQuery ? 'w-48' : 'w-10'
              } h-10`}
            >
              <button
                className="flex items-center justify-center w-10 h-10 text-white/80 hover:text-white"
                type="button"
                onMouseEnter={() => setIsSearchExpanded(true)}
                onMouseLeave={() => !searchQuery && setIsSearchExpanded(false)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </button>
              <input
                id="header-search"
                name="q"
                type="text"
                role="searchbox"
                aria-label="Search"
                placeholder="Search..."
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => setIsSearchExpanded(true)}
                onBlur={() => !searchQuery.trim() && setIsSearchExpanded(false)}
                onKeyPress={handleSearchKeyPress}
                onMouseEnter={() => setIsSearchExpanded(true)}
                onMouseLeave={() => !searchQuery && setIsSearchExpanded(false)}
                className={`flex-1 px-3 py-2 text-sm text-white bg-dark-gray rounded-full border border-gray-border outline-none transition-all duration-300 placeholder-white/50 ${
                  isSearchExpanded || searchQuery ? 'opacity-100' : 'opacity-0 w-0'
                }`}
              />
            </div>
          </div>

          {/* Mobile search icon */}
          <button
            className="text-white md:hidden hover:text-accent"
            onClick={() => navigate('/search')}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </button>

          {/* Auth */}
          <div className="relative z-20 flex items-center">
            {isLoading ? (
              <div className="w-6 h-6 border-2 border-white rounded-full border-t-transparent animate-spin" />
            ) : isAuthenticated ? (
              <div className="flex items-center">
                {currentProfile ? (
                  <ProfileDropdown />
                ) : (
                  <button
                    onClick={handleLogout}
                    className="bg-accent hover:bg-accent-hover text-primary px-4 py-2 text-sm font-display uppercase border-0 cursor-pointer transition-colors"
                  >
                    Logout
                  </button>
                )}
              </div>
            ) : (
              <button
                onClick={handleLogin}
                className="bg-accent hover:bg-accent-hover text-primary px-4 py-2 text-sm font-display uppercase border-0 cursor-pointer transition-colors"
              >
                Log In
              </button>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden bg-transparent border-0 cursor-pointer p-0 text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {mobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile menu dropdown */}
      {mobileMenuOpen && (
        <div className="md:hidden mt-4 pb-4 border-t border-white/10">
          <nav className="flex flex-col space-y-3 pt-4">
            <button className={navLinkClass('/')} onClick={handleHomeClick}>
              Home
            </button>
            <button className={navLinkClass('/events')} onClick={handleEventsClick}>
              Events
            </button>
            <button className={navLinkClass('/fighters')} onClick={handleFightersClick}>
              Fighters
            </button>
            <button className={navLinkClass('/my-lists')} onClick={handleMyListsClick}>
              My List
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
